import React from "react";
import {
  usePrimaryOrganization,
  usePrimaryOrganizationId,
  useSelfOrganizationUser,
} from "../../../hooks/useOrganization";
import { RouteHelpers, Routes } from "../../../root/routes";
import { BaseNav } from "./BaseNav";
import {
  DIVIDER_LITERAL,
  isNavSection,
  NAV_ITEMS,
  NAV_ITEMS_GENERAL_ONLY,
  NAV_ITEMS_HR_ONLY,
  NavDivider,
  NavItem,
  NavSection,
} from "./config";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";

import { NetworkRequest } from "../../../../common/models";
import { UserRole } from "../../../../common/service/server/Types";
import { useRequestSummaries, useSelf } from "../../../hooks";
import { NavSectionLabel, SidebarNavLinkRedesign } from "@truevault/aquilo";
import {
  createFakeCompletedOnboardingProgress,
  isPrivacyCenterOnboardingComplete,
  isWorkflowsOnboardingComplete,
  TruevaultOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";
import {
  isHrCenterEnabled,
  isHrDsarWorkflowsEnabled,
  isHrNoticesEnabled,
} from "../../../pages/stay-compliant/hr-center/HRCenterDashboardPage";
import {
  isDSARFormsEnabled,
  isPrivacyCenterDeveloperInstructionsEnabled,
  isWebsiteConsentEnabled,
} from "../../../pages/stay-compliant/PrivacyCenterDashboard";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../../pages/stay-compliant/PrivacyNoticeChecklistItems";
import { DevInstructionsDSARFormsOnboardingSlug } from "../../../pages/stay-compliant/privacy-center/DSARForms";
import { GLOBAL_ADMIN_EDIT_BAR_HEIGHT_PX } from "../GlobalAdminEditBar";
import { DevInstructionsFinishedDataMapOnboarding } from "../../../pages/data-map/DataMapDashboardPage";
import { useVisibleFlaggedBusinessProfileQuestions } from "../../../hooks/useVisibleFlaggedBusinessProfileQuestions";
import { isDataRetentionNavDisabled } from "./dataMapNavGating";

export const isRequestInboxEnabled = (
  org: OrganizationDto,
  onboardingProgress: TruevaultOnboardingProgress,
) => {
  // Always enable if pending requests
  if (org?.activeRequestCount > 0) {
    return true;
  }

  // Enable if workflows onboarding is complete
  return (
    onboardingProgress &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const isTaskListEnabled = (
  org: OrganizationDto,
  onboardingProgress: TruevaultOnboardingProgress,
) => {
  // Always enable if pending tasks
  if (org?.activeTaskCount > 0) {
    return true;
  }

  // Enable if workflows onboarding is complete
  return (
    onboardingProgress &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

const getIncompleteSetupTaskCount = ({
  organization,
  userRole,
  onboardingProgress,
  isDSARFormsOnboardingComplete,
  hasFlaggedBusinessProfileQuestions,
}: {
  organization: OrganizationDto;
  userRole: UserRole | undefined;
  onboardingProgress: TruevaultOnboardingProgress;
  isDSARFormsOnboardingComplete: boolean | undefined;
  hasFlaggedBusinessProfileQuestions: boolean | undefined;
}): number => {
  const {
    workflowsSetupOnboardingProgress,
    privacyCenterOnboardingProgress,
    dataMapOnboardingProgress,
    surveyOnboardingProgress,
    hrCenterOnboardingProgress,
  } = onboardingProgress;

  // Keep this gate in sync with useDashboardSetupTasks. These items are only
  // added to the To-Do List after the one-time Workflow Setup is complete.
  if (!workflowsSetupOnboardingProgress.isFinishedWorkflowSetupOnboarding || !userRole) {
    return 0;
  }

  const completionStatuses: boolean[] = [];
  const isHrUser = userRole === "HR";

  if (!isHrUser) {
    completionStatuses.push(
      workflowsSetupOnboardingProgress.isFinishedWithApiIntegrationsSetup,
      workflowsSetupOnboardingProgress.isFinishedDataStorageOnboarding,
      workflowsSetupOnboardingProgress.isLimitSettingsComplete,
    );

    if (userRole !== "GENERAL") {
      completionStatuses.push(privacyCenterOnboardingProgress.isFinishedWithPoliciesNotices);
    }

    completionStatuses.push(
      // This status is not currently in the onboarding-progress response, but
      // the navbar already loads the developer-instructions survey.
      isDSARFormsOnboardingComplete ?? true,
      privacyCenterOnboardingProgress.developerInstructionsOnboardingProgress
        .isFinishedDevInstructionsSurvey &&
        privacyCenterOnboardingProgress.developerInstructionsOnboardingProgress
          .isConsentManagementDevInstructionsComplete,
      privacyCenterOnboardingProgress.isWebsiteConsentSetup,
      surveyOnboardingProgress.isBusinessSurveyFinished &&
        hasFlaggedBusinessProfileQuestions !== true,
      dataMapOnboardingProgress.isVendorInventoryComplete,
      dataMapOnboardingProgress.isDataFlowsComplete,
      dataMapOnboardingProgress.isVendorDocumentationComplete,
      dataMapOnboardingProgress.isDataRetentionComplete,
    );
  }

  if (organization.requiresEmployeeMapping && userRole !== "GENERAL") {
    completionStatuses.push(
      hrCenterOnboardingProgress.isAllHrDataMapCompleted &&
        hrCenterOnboardingProgress.isHrDataFlowsFinished,
      hrCenterOnboardingProgress.isFinishedWithHrDsarWorkflows,
      hrCenterOnboardingProgress.isHrNoticesPublished,
    );
  }

  return completionStatuses.filter((isComplete) => !isComplete).length;
};

export const StayCompliantNav: React.FC = () => {
  const [self] = useSelf();
  const [organization, request, refreshOrg] = usePrimaryOrganization();
  const currentOrgUser = useSelfOrganizationUser();
  const primaryOrgId = usePrimaryOrganizationId();
  const [dataRequestPage, ,] = useRequestSummaries(primaryOrgId, "active");

  const showGlobalAdminEditBar = self?.globalRole === "ADMIN";
  const isGeneralUser = currentOrgUser?.role === "GENERAL";
  const isHrUser = currentOrgUser?.role === "HR";

  const [remoteOnboardingProgress] = useOnboardingProgress(primaryOrgId);
  const hasFlaggedBusinessProfileQuestions = useVisibleFlaggedBusinessProfileQuestions();

  const { answers: devInstructionsAnswers } = useSurvey(
    primaryOrgId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const isDSARFormsOnboardingComplete =
    devInstructionsAnswers &&
    devInstructionsAnswers[DevInstructionsDSARFormsOnboardingSlug] === "true";

  const isDataMapOnboardingComplete =
    devInstructionsAnswers &&
    devInstructionsAnswers[DevInstructionsFinishedDataMapOnboarding] === "true";
  // Don't check for `onboardingProgress` here since we don't want to block nav bar rendering on the onboarding progress request.
  if (!NetworkRequest.areFinished(request)) {
    return null;
  }

  // Don't block side bar nav rendering on `remoteOnboardingProgress` to fully load (since it fetches the full data map), so eagerly render the nav bar assuming all steps are complete
  const onboardingProgress = remoteOnboardingProgress ?? createFakeCompletedOnboardingProgress();

  const {
    workflowsSetupOnboardingProgress: {
      isFinishedWithApiIntegrationsSetup,
      isFinishedDataStorageOnboarding,
      isFinishedWorkflowSetupOnboarding,
      isLimitSettingsComplete,
    },
    dataMapOnboardingProgress: {
      isDataRetentionComplete,
      isVendorInventoryComplete,
      isDataFlowsComplete,
      isVendorDocumentationComplete,
      isVendorDocumentationSkipped,
    },
    surveyOnboardingProgress: { isBusinessSurveyFinished },
    privacyCenterOnboardingProgress: { isFinishedWithPoliciesNotices },
  } = onboardingProgress;
  const isVendorDocumentationProgressionComplete =
    isVendorDocumentationComplete || isVendorDocumentationSkipped;

  const incompleteSetupTaskCount = remoteOnboardingProgress
    ? getIncompleteSetupTaskCount({
        organization,
        userRole: currentOrgUser?.role,
        onboardingProgress: remoteOnboardingProgress,
        isDSARFormsOnboardingComplete,
        hasFlaggedBusinessProfileQuestions,
      })
    : 0;

  const isHrCenterSetupRequired = organization?.requiresEmployeeMapping === true;

  // For now, only check the retention finished to determine when the data map onboarding is complete, since we'll let users leave the Vendor Documentation step as un-finished (and the data retention step can only be complete after the preceding steps are done)
  // Since `privacyCenterOnboardingProgress?.isFinishedWithPoliciesNotices` is a "one-way" completion step and can't be undone, if the org has completed that step then we can assume they've completed the data map onboarding
  let areAllDataMapStepsComplete = isDataRetentionComplete || isFinishedWithPoliciesNotices;

  const navItemsConfig = navItemsForRole(currentOrgUser?.role);

  const navItems = (org: OrganizationDto) =>
    navItemsConfig.map((navItem: NavItem | NavDivider | NavSection, idx: number) => {
      if (isNavSection(navItem)) {
        return <NavSectionLabel key={`section-${idx}`}>{navItem.sectionLabel}</NavSectionLabel>;
      }
      if (navItem === DIVIDER_LITERAL) {
        // The redesigned nav separates groups with whitespace instead of divider lines
        return <div key={`divider-${idx}`} style={{ height: "12px" }} />;
      }

      const { item, hidden } = navItem as NavItem;
      // Make a deep copy of navItem.children since it may be modified
      let navItemChildrenMutable = JSON.parse(JSON.stringify(navItem.children || [])) as NavItem[];

      const isHidden =
        typeof hidden == "function" ? hidden(org) : typeof hidden == "undefined" ? false : hidden;

      let notificationCount = 0;

      // Don't show request inbox notifications for HR users since they only see HR requests
      if (item == "REQUEST_INBOX" && !isHrUser) {
        notificationCount = dataRequestPage?.meta?.totalCount || org.activeRequestCount;
      }
      if (item == "TASK_LIST") {
        notificationCount = org.activeTaskCount + incompleteSetupTaskCount;
      }
      // Hide the HR Center nav item if the org has no employee collection groups
      if (item == "HR_CENTER" && !isHrCenterSetupRequired) {
        return null;
      }

      if (isHidden) {
        return null;
      }

      // Before essential privacy center steps are complete, lock all nav items except: privacy center, knowledge center, data map, and Ramsay (feature-flagged, independent of onboarding)
      // Note that since the privacy center onboarding progress may become incomplete with new developer instructions modules, we also unlock the top-level nav items if the API integrations setup is complete (since that setup is one-time)
      let isDisabled = false;
      if (
        !(
          item == "DASHBOARD" ||
          item == "KNOWLEDGE_CENTER" ||
          item == "PRIVACY_CENTER" ||
          item == "DATA_MAP_DASHBOARD" ||
          item == "RAMSAY"
        )
      ) {
        isDisabled = !(
          isPrivacyCenterOnboardingComplete(
            onboardingProgress.privacyCenterOnboardingProgress,
            /* onlyEssentialSteps = */ true,
          ) || isFinishedWithApiIntegrationsSetup
        );
      }
      // Unlock the "Documents" page after the first step in privacy center onboarding
      if (item == "DOCUMENTS") {
        isDisabled =
          !onboardingProgress.privacyCenterOnboardingProgress.isFinishedWithPoliciesNotices;
      }

      if (item == "TASK_LIST") {
        isDisabled = !isTaskListEnabled(org, onboardingProgress);
      }

      if (item == "REQUEST_INBOX") {
        isDisabled = !isRequestInboxEnabled(org, onboardingProgress);
      }

      // For the Privacy Center top-level nav item, enable/disable the individual privacy center items based on the setup status and current user's role
      if (item == "PRIVACY_CENTER") {
        isDisabled = !areAllDataMapStepsComplete && !isFinishedWithPoliciesNotices;

        for (let child of navItemChildrenMutable) {
          if (child.item == "PRIVACY_CENTER_POLICIES_AND_NOTICES") {
            // This step is always enabled for non-general users since it's the first step
            child.isDisabled = isGeneralUser;
            // Only enable subsequent steps if all the previous steps are completed
          } else if (child.item == "PRIVACY_CENTER_DSAR_FORMS") {
            child.isDisabled = !isDSARFormsEnabled(
              onboardingProgress.privacyCenterOnboardingProgress,
              isDSARFormsOnboardingComplete,
            );
          } else if (child.item == "PRIVACY_CENTER_WEBSITE_CONSENT") {
            child.isDisabled = !isWebsiteConsentEnabled(
              onboardingProgress.privacyCenterOnboardingProgress,
            );
          } else if (child.item == "PRIVACY_CENTER_DEVELOPER_INSTRUCTIONS") {
            child.isDisabled = !isPrivacyCenterDeveloperInstructionsEnabled(
              onboardingProgress.privacyCenterOnboardingProgress,
              isDSARFormsOnboardingComplete,
            );
          }
        }
      }

      // Similarly for HR Center, enable/disable individual items based on setup status
      if (item === "HR_CENTER") {
        const hrProgress = onboardingProgress.hrCenterOnboardingProgress;

        isDisabled = !isHrCenterEnabled(onboardingProgress, currentOrgUser?.role);

        for (let child of navItemChildrenMutable) {
          if (child.item == "HR_CENTER_HR_DATA_MAP") {
            // This step is always enabled since it's the first step
            child.isDisabled = false;
          } else if (child.item == "HR_CENTER_DSAR_WORKFLOWS") {
            child.isDisabled = !isHrDsarWorkflowsEnabled(hrProgress);
          } else if (child.item == "HR_CENTER_HR_NOTICES") {
            child.isDisabled = !isHrNoticesEnabled(hrProgress);
          }
        }
      }

      if (item === "DATA_MAP_DASHBOARD") {
        for (let child of navItemChildrenMutable) {
          if (child.item == "VENDOR_INVENTORY") {
            // When in onboarding, only enable Vendor List after Business Profile is complete
            if (!isDataMapOnboardingComplete) {
              child.isDisabled = !isBusinessSurveyFinished && !isVendorInventoryComplete;
            }
          } else if (child.item == "DATA_FLOWS") {
            if (!isDataMapOnboardingComplete) {
              child.isDisabled = !isVendorInventoryComplete;
            }
          } else if (child.item == "VENDOR_DOCUMENTATION") {
            // When in onboarding, only enable Vendor Documention item when it's the next step (i.e. all previous steps have been completed)
            if (!isDataMapOnboardingComplete) {
              child.isDisabled = !isVendorInventoryComplete || !isDataFlowsComplete;
            }
          } else if (child.item == "DATA_RETENTION") {
            // When in onboarding, only enable the Data Retention item when it's the next step (i.e. all previous steps have been completed)
            if (!isDataMapOnboardingComplete) {
              child.isDisabled = isDataRetentionNavDisabled({
                isVendorInventoryComplete,
                isDataFlowsComplete,
                isVendorDocumentationProgressionComplete,
              });
            }
          }
        }
      }

      if (item === "WORKFLOWS") {
        // If the API integrations setup is already complete, then always enable the top-level workflows nav item
        isDisabled =
          !isPrivacyCenterOnboardingComplete(onboardingProgress.privacyCenterOnboardingProgress) &&
          !isFinishedWithApiIntegrationsSetup;

        for (let child of navItemChildrenMutable) {
          if (child.item == "WORKFLOWS_API_INTEGRATIONS") {
            // This step is always enabled since it's the first step
            child.isDisabled = false;
          } else if (child.item == "WORKFLOWS_DATA_STORAGE_SETTINGS") {
            child.isDisabled = !(
              isFinishedWithApiIntegrationsSetup || isFinishedDataStorageOnboarding
            );
          } else if (child.item == "WORKFLOWS_REQUESTS_WORKFLOWS") {
            child.isDisabled = !(
              isFinishedDataStorageOnboarding || isFinishedWorkflowSetupOnboarding
            );
            child.route =
              isFinishedWorkflowSetupOnboarding && !isLimitSettingsComplete
                ? Routes.organization.requestHandling.requestToLimit
                : Routes.organization.requestHandling.base;
          } else if (child.item == "WORKFLOWS_EMAIL_TEMPLATES") {
            child.isDisabled = !isFinishedWorkflowSetupOnboarding;
          }
        }
      }

      // Force unlock of all nav items after org has completed onboarding latch (POL-7563)
      if (org.finishedOnboardingLatch) {
        isDisabled = false;
        navItemChildrenMutable.forEach((child) => {
          child.isDisabled = false;
        });
      }

      return (
        <SidebarNavLinkRedesign
          key={item}
          isDisabled={isDisabled}
          notificationCount={notificationCount}
          refresh={refreshOrg}
          navItem={{ ...navItem, children: navItemChildrenMutable }}
          org={org}
        />
      );
    });

  const navContent = (org?: OrganizationDto) => {
    return org && <div className="tw-boundary">{navItems(org)}</div>;
  };

  return (
    <BaseNav
      showUserNavItem
      useNewDesign
      settingsRoute={
        currentOrgUser?.role === "HR" ? null : RouteHelpers.organization.settings(organization?.id)
      }
      userProfileRoute={Routes.user.profile}
      navContent={navContent(organization)}
      bottomInset={showGlobalAdminEditBar ? GLOBAL_ADMIN_EDIT_BAR_HEIGHT_PX : 0}
    />
  );
};

const navItemsForRole = (role: UserRole | undefined): (NavItem | NavDivider | NavSection)[] => {
  switch (role) {
    case "ADMIN":
      return NAV_ITEMS;
    case "GENERAL":
      return NAV_ITEMS_GENERAL_ONLY;
    case "HR":
      return NAV_ITEMS_HR_ONLY;
    default:
      return [];
  }
};
